import { Box, Breadcrumbs, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import FoodBoxTopComponent from './FoodBoxTopComponent';
import PlannerFooter from '../../../../Components/menu_planner/PlannerFooter';
import FoodBoxGridList from './FoodBoxGridList';
import { deleteMeal, getAllMealsData } from '../create_food_box/CreateFoodBoxApi';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import LoaderProgressComponent from '../../../../Components/menu_planner/LoaderProgressComponent';
import FoodBoxTable from './FoodBoxTable';
import FoodBoxSummaryActionComponent from './FoodBoxSummaryActionComponent';
import SummarySearchAndFilterComponent from './SummarySearchComponent';
import DeleteMealDialog, { getScheduleKey } from './DeleteMealDialog';
import { MealAction } from '../../../../store/meal-slice';
import { queryKeys } from '../../../../utils/queryKeys';
import {
  deleteSchedule,
  getFutureSchedulesForMeal,
} from '../../schedules/ScheduleApi';

const getMealErrorMessage = (error, fallbackMessage) => {
  return error?.response?.data?.message ?? fallbackMessage;
};

const isRegularMeal = (meal) => {
  return !(
    meal?.isCateringFood ||
    meal?.isAddOnItem ||
    meal?.isDefaultLunch ||
    meal?.isDefaultDinner
  );
};

const FoodBoxScreen = () => {
  const [mealToDelete, setMealToDelete] = useState(null);
  const [deletingScheduleKey, setDeletingScheduleKey] = useState(null);

  const { isViewAsTable } = useSelector((state) => state.boxSummary);
  const { language } = useSelector((state) => state.globalUi);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { isLoading } = useQuery(
    queryKeys.getMealData,
    getAllMealsData,
    {
      onSuccess: (response) => {
        console.log(queryKeys.getMealData);
        console.dir(response.data);
        dispatch(MealAction.addAllMeals(response.data));
      },
      refetchOnWindowFocus: false,
      cacheTime: 5 * 60 * 1000, //Milliseconds
    }
  );

  const requiresScheduleCleanup = isRegularMeal(mealToDelete);

  const mealPlanNameById = useMemo(() => {
    const mealPlans = user?.canteen?.mealPlans ?? {};

    return Object.values(mealPlans).flat().reduce((accumulator, mealPlan) => {
      if (mealPlan?.id) {
        accumulator[mealPlan.id] = mealPlan.name ?? mealPlan.id;
      }

      return accumulator;
    }, {});
  }, [user?.canteen?.mealPlans]);

  const futureSchedulesQueryKey = [
    queryKeys.getFutureMealSchedules,
    mealToDelete?.id,
    mealToDelete?.externalId,
    language,
  ];

  const {
    data: futureSchedules = [],
    isLoading: isLoadingFutureSchedules,
    isError: isFutureSchedulesError,
  } = useQuery(
    futureSchedulesQueryKey,
    () =>
      getFutureSchedulesForMeal({
        mealId: mealToDelete?.id,
      }),
    {
      enabled: !!mealToDelete && requiresScheduleCleanup,
      refetchOnWindowFocus: false,
    }
  );

  const futureSchedulesWithMealPlanNames = useMemo(() => {
    return futureSchedules.map((schedule) => {
      const mealPlanId =
        schedule?.mealPlanId ?? schedule?.mealPlan?.id ?? schedule?.mealPlan;

      return {
        ...schedule,
        mealPlanName:
          schedule?.mealPlanName ??
          mealPlanNameById[mealPlanId] ??
          mealPlanId ??
          '-',
      };
    });
  }, [futureSchedules, mealPlanNameById]);

  const deleteMealMutation = useMutation(deleteMeal, {
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(queryKeys.getMealData),
        queryClient.invalidateQueries(queryKeys.getAllScheduleData),
      ]);
      toast.success('Food box deleted successfully');
      setMealToDelete(null);
    },
    onError: (error) => {
      toast.error(getMealErrorMessage(error, 'Unable to delete food box'));
    },
  });

  const deleteScheduleMutation = useMutation(deleteSchedule);

  useEffect(() => {
    dispatch(MealAction.reset());
  }, [dispatch]);

  const handleDeleteMeal = async () => {
    const mealIdentifier = mealToDelete?.id ?? mealToDelete?.externalId;

    if (
      !mealIdentifier ||
      deleteMealMutation.isLoading ||
      (requiresScheduleCleanup && isLoadingFutureSchedules) ||
      (requiresScheduleCleanup && isFutureSchedulesError) ||
      deletingScheduleKey !== null ||
      (requiresScheduleCleanup && futureSchedulesWithMealPlanNames.length > 0)
    ) {
      return;
    }

    try {
      await deleteMealMutation.mutateAsync(mealIdentifier);
    } catch (error) {
      // Mutation-level error handling already shows the toast.
    }
  };

  const handleDeleteSchedule = async (schedule) => {
    const scheduleDate = schedule?.date ?? schedule?.dateOfSchedule;
    const scheduleMealPlanId =
      schedule?.mealPlanId ?? schedule?.mealPlan?.id ?? schedule?.mealPlan;

    if (
      !requiresScheduleCleanup ||
      !scheduleDate ||
      !schedule?.scheduleType ||
      !scheduleMealPlanId
    ) {
      return;
    }

    const nextDeletingScheduleKey = getScheduleKey(schedule);
    setDeletingScheduleKey(nextDeletingScheduleKey);

    try {
      await deleteScheduleMutation.mutateAsync({
        date: scheduleDate,
        scheduleType: schedule.scheduleType,
        mealPlanId: scheduleMealPlanId,
        mealId:
          schedule.externalId ??
          mealToDelete?.externalId ??
          mealToDelete?.id,
      });

      queryClient.setQueryData(futureSchedulesQueryKey, (currentSchedules = []) =>
        currentSchedules.filter(
          (currentSchedule) =>
            getScheduleKey(currentSchedule) !== nextDeletingScheduleKey
        )
      );
      await queryClient.invalidateQueries(queryKeys.getAllScheduleData);
      toast.success('Scheduled meal deleted successfully');
    } catch (error) {
      toast.error(getMealErrorMessage(error, 'Unable to delete schedule'));
    } finally {
      setDeletingScheduleKey(null);
    }
  };

  return (
    <Box>
      <Box
        className='content-horizontal-margin'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: 3,
          mt: 3,
          mb: 3,
        }}
      >
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography variant='s16_w500_orange'>Food Boxes</Typography>
        </Breadcrumbs>
        <FoodBoxTopComponent />
        <FoodBoxSummaryActionComponent />
        {!isViewAsTable ? <SummarySearchAndFilterComponent /> : null}
        {isLoading ? (
          <LoaderProgressComponent />
        ) : isViewAsTable ? (
          <FoodBoxTable
            onDeleteMealClick={setMealToDelete}
            isDeletingMeal={deleteMealMutation.isLoading}
          />
        ) : (
          <FoodBoxGridList
            onDeleteMealClick={setMealToDelete}
            isDeletingMeal={deleteMealMutation.isLoading}
          />
        )}
      </Box>
      <DeleteMealDialog
        open={!!mealToDelete}
        onClose={() => {
          if (!deleteMealMutation.isLoading && deletingScheduleKey === null) {
            setMealToDelete(null);
          }
        }}
        onConfirm={handleDeleteMeal}
        onDeleteSchedule={handleDeleteSchedule}
        meal={mealToDelete}
        language={language}
        requiresScheduleCleanup={requiresScheduleCleanup}
        isDeleting={deleteMealMutation.isLoading}
        futureSchedules={futureSchedulesWithMealPlanNames}
        isLoadingFutureSchedules={isLoadingFutureSchedules}
        isFutureSchedulesError={isFutureSchedulesError}
        deletingScheduleKey={deletingScheduleKey}
      />
      <PlannerFooter />
    </Box>
  );
};

export default FoodBoxScreen;
